import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { PrimaryButton, SecondaryButton } from "@/components/common";
import { CommonDialog } from "@/components/common/CommonDialog";

import type { WearableRemovableItem } from "../wearableRemovableItems";

export interface WearableRemoveDayDataDialogProps {
  open: boolean;
  periodLabel: string;
  items: WearableRemovableItem[];
  /** patient = tela do paciente; professional = atendimento (prontuário de terceiro). */
  audience?: "patient" | "professional";
  loading?: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
}

function buildRemovalCopy(
  observationCount: number,
  audience: "patient" | "professional",
): { intro: string; outro: string } {
  const countLabel = observationCount.toLocaleString("pt-BR");
  const registroLabel = observationCount === 1 ? "registro" : "registros";
  const isProfessional = audience === "professional";

  const intro =
    observationCount === 1
      ? isProfessional
        ? "Este registro não estará mais visível no prontuário do paciente."
        : "Este registro não estará mais visível no seu prontuário."
      : isProfessional
        ? `Estes ${countLabel} ${registroLabel} não estarão mais visíveis no prontuário do paciente.`
        : `Estes ${countLabel} ${registroLabel} não estarão mais visíveis no seu prontuário.`;

  const outro = isProfessional
    ? "Para continuar acompanhando a evolução, será necessário que o paciente envie novos dados."
    : "Para que o profissional continue acompanhando sua evolução, será necessário enviar novos dados.";

  return { intro, outro };
}

export function WearableRemoveDayDataDialog({
  open,
  periodLabel,
  items,
  audience = "patient",
  loading = false,
  onClose,
  onConfirm,
}: WearableRemoveDayDataDialogProps) {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(itemIds));
  }, [open, itemIds]);

  const selectedCount = selectedIds.size;
  const totalCount = items.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const noneSelected = selectedCount === 0;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const toggleItem = (id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(itemIds));
  const clearAll = () => setSelectedIds(new Set());

  const { intro, outro } = buildRemovalCopy(selectedCount, audience);

  const footer = (
    <Stack
      direction="column-reverse"
      spacing={1.5}
      width="100%"
      alignItems="stretch"
    >
      <SecondaryButton
        width="100%"
        height="44px"
        onClick={handleClose}
        disabled={loading}
      >
        Cancelar
      </SecondaryButton>
      <PrimaryButton
        width="100%"
        height="44px"
        onClick={() => onConfirm(Array.from(selectedIds))}
        disabled={loading || selectedCount <= 0}
        loading={loading}
      >
        Remover dados
      </PrimaryButton>
    </Stack>
  );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={handleClose}
      maxWidth="sm"
      sheetTitle="Remover dados do dia?"
      sheetSubtitle={periodLabel}
      footer={footer}
    >
      <Stack spacing={1.5}>
        <Typography
          variant="body1"
          color="neutral700.main"
          sx={{ lineHeight: 1.65 }}
        >
          {selectedCount > 0 ? (
            <>
              {intro} {outro}
            </>
          ) : (
            "Selecione os registros que deseja remover."
          )}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary800.main, 0.04),
          }}
        >
          <FormControlLabel
            sx={{ mr: 0, ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={!allSelected && !noneSelected}
                disabled={loading || totalCount === 0}
                onChange={() => (allSelected ? clearAll() : selectAll())}
                inputProps={{ "aria-label": "Selecionar todos os registros" }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                {allSelected ? "Limpar seleção" : "Selecionar tudo"}
              </Typography>
            }
          />
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ whiteSpace: "nowrap", pr: 0.5 }}
          >
            {selectedCount} de {totalCount}
          </Typography>
        </Stack>

        <Box
          sx={{
            maxHeight: 280,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "neutral200.main",
            borderRadius: 2,
          }}
        >
          <Stack divider={undefined} spacing={0}>
            {items.map((item) => {
              const checked = selectedIds.has(item.id);
              return (
                <FormControlLabel
                  key={item.id}
                  sx={{
                    m: 0,
                    px: 1.25,
                    py: 0.75,
                    alignItems: "flex-start",
                    borderBottom: "1px solid",
                    borderColor: "neutral200.main",
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={loading}
                      onChange={() => toggleItem(item.id)}
                      sx={{ mt: 0.25 }}
                    />
                  }
                  label={
                    <Stack spacing={0.25} sx={{ minWidth: 0, pt: 0.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.label}
                      </Typography>
                      {item.hint ? (
                        <Typography variant="caption" color="text.secondary">
                          {item.hint}
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </CommonDialog>
  );
}
